import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChatCircleDots, X } from "@phosphor-icons/react";

/**
 * ContactTruckButton — Pro-only action button on `/t/:slug` that opens
 * an inquiry modal. Submissions go through `/api/profile/:slug/contact`
 * which relays to the owner's contact email via Resend (owner's address
 * never published, rate-limited, honeypot-protected).
 *
 * Visually mirrors the rest of the profile action row (Directions /
 * Follow / Notify / Share) — same height, rounded corners, ghost
 * tone, stacked icon + label.
 */
export default function ContactTruckButton({ slug, truckName, variant = "profile", onOpen }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  // Honeypot — kept in component state but never shown to users. Real
  // users will leave this empty; bots will fill it.
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);

  const openModal = () => {
    onOpen?.();
    setOpen(true);
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !email.trim() || message.trim().length < 10) {
      toast.error("Please fill out every field — message must be at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/profile/${slug}/contact`, {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        website,
      });
      toast.success(`Message sent to ${truckName || "the truck"}.`);
      setOpen(false);
      setName("");
      setEmail("");
      setMessage("");
      setWebsite("");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("You've sent a few messages already — please try again later.");
      } else if (status === 503) {
        toast.error("This truck hasn't set up a contact email yet.");
      } else {
        toast.error(formatApiError(err, "Could not send message."));
      }
    } finally {
      setBusy(false);
    }
  };

  // Compact "sheet" variant — used on the map pin card where the
  // surrounding sheet has a dark/orange background; matches the
  // sibling Directions + Order Online buttons (h-11, rounded-full).
  // Default "profile" variant remains the original ghost-card style
  // used by the public profile action row.
  const triggerClass = variant === "sheet"
    ? "h-11 rounded-full border border-white/30 text-white hover:bg-white/10 font-semibold text-sm transition flex items-center justify-center gap-2"
    : "h-12 rounded-2xl flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-card border border-border hover:bg-muted text-foreground";

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="action-contact"
        className={triggerClass}
      >
        <ChatCircleDots weight="duotone" className="h-4 w-4" />
        Contact
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] grid place-items-center px-4 py-8 bg-black/60 backdrop-blur-sm animate-in fade-in"
          role="dialog"
          aria-modal="true"
          onClick={close}
          data-testid="contact-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl p-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-primary font-bold">
                  Send {truckName || "the truck"} a message
                </div>
                <h2 className="font-display font-bold text-xl mt-1 leading-tight">
                  Contact this truck
                </h2>
              </div>
              <button
                type="button"
                onClick={close}
                disabled={busy}
                aria-label="Close"
                data-testid="contact-modal-close"
                className="flex-none h-8 w-8 grid place-items-center rounded-full hover:bg-muted transition"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Your message goes straight to the owner&apos;s inbox. They&apos;ll reply to you by email.
            </p>

            <form onSubmit={submit} className="mt-4 space-y-3">
              {/* Honeypot — visually hidden, off-screen, untabbable.
                  Bots see a normal input and fill it; humans never do. */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
              >
                <label htmlFor="contact-website">Website (leave blank)</label>
                <input
                  id="contact-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="contact-name" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Your name
                </label>
                <input
                  id="contact-name"
                  type="text"
                  required
                  maxLength={80}
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full h-11 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Your email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full h-11 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
                  placeholder="Catering inquiry, festival booking, general question…"
                  data-testid="contact-message-input"
                />
                <div className="text-[11px] text-muted-foreground mt-1 text-right tabular-nums">
                  {message.length}/2000
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={close}
                  disabled={busy}
                  className="h-10 px-4 rounded-full border border-border bg-card text-sm font-semibold text-muted-foreground hover:text-foreground transition"
                  data-testid="contact-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="h-10 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition disabled:opacity-60 disabled:cursor-wait"
                  data-testid="contact-submit"
                >
                  {busy ? "Sending…" : "Send message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
